import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Plus, Trash2, Save, User as UserIcon, CalendarOff, Plane, ThermometerSnowflake, GraduationCap, Upload, Instagram, Facebook, Linkedin, Globe, Phone, Lock } from "lucide-react";

const BLOCK_TYPES = [
  { value: "vacation", label: "Urlaub", icon: Plane, color: "#8B9D83" },
  { value: "sick", label: "Krank", icon: ThermometerSnowflake, color: "#b91c1c" },
  { value: "training", label: "Fortbildung", icon: GraduationCap, color: "#3b82f6" },
  { value: "other", label: "Sonstiges", icon: CalendarOff, color: "#6b7280" },
];

export default function StaffManager() {
  const { t } = useAuth();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [newBlock, setNewBlock] = useState({ start_date: "", end_date: "", type: "vacation", reason: "" });
  const [limit, setLimit] = useState(null);

  const load = () =>
    Promise.all([
      api.get("/cms/staff").then(({ data }) => setItems(data)),
      api.get("/cms/staff/limit").then(({ data }) => setLimit(data)).catch(() => {}),
    ]);
  useEffect(() => { load(); }, []);

  const loadBlocks = async (staffId) => {
    if (!staffId) { setBlocks([]); return; }
    const { data } = await api.get(`/cms/staff/${staffId}/blocks`);
    setBlocks(data || []);
  };

  const openEdit = (s) => { setEditing(s); loadBlocks(s?.id); };

  const save = async () => {
    const payload = {
      name: editing.name, email: editing.email || null,
      role: editing.role || "Stylist", photo_url: editing.photo_url || null, active: !!editing.active,
      bio: editing.bio || null, phone: editing.phone || null,
      instagram_url: editing.instagram_url || null, facebook_url: editing.facebook_url || null,
      tiktok_url: editing.tiktok_url || null, linkedin_url: editing.linkedin_url || null,
      website_url: editing.website_url || null,
      user_email: editing.user_email || null,
      user_password: editing.user_password || null,
      user_role: editing.user_role || "staff_member",
    };
    try {
      if (editing.id) { await api.put(`/cms/staff/${editing.id}`, payload); setEditing(null); load(); }
      else { const { data } = await api.post("/cms/staff", payload); setEditing(data); loadBlocks(data.id); load(); }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "staff_limit_reached") {
        window.alert(detail.message || "Paket-Limit für Mitarbeitende erreicht.");
      } else {
        window.alert(typeof detail === "string" ? detail : "Speichern fehlgeschlagen.");
      }
    }
  };
  const remove = async (id) => { if (!window.confirm("Mitarbeiter löschen? Auch alle Abwesenheiten werden entfernt.")) return; await api.delete(`/cms/staff/${id}`); load(); };

  const addBlock = async () => {
    if (!editing?.id || !newBlock.start_date || !newBlock.end_date) return;
    if (newBlock.end_date < newBlock.start_date) { alert("Endedatum muss >= Startdatum sein"); return; }
    await api.post(`/cms/staff/${editing.id}/blocks`, newBlock);
    setNewBlock({ start_date: "", end_date: "", type: "vacation", reason: "" });
    loadBlocks(editing.id);
  };
  const removeBlock = async (bid) => {
    if (!window.confirm("Abwesenheit löschen?")) return;
    await api.delete(`/cms/staff-blocks/${bid}`);
    loadBlocks(editing.id);
  };

  if (editing) {
    return (
      <div className="p-8 lg:p-12 mv-fade-in max-w-2xl">
        <div className="flex items-end justify-between mb-8">
          <h1 className="font-display text-4xl">{editing.id ? `${t("staff")} bearbeiten` : `Neuer ${t("staff")}`}</h1>
          <div className="flex gap-2">
            <button onClick={() => setEditing(null)} className="mv-btn-ghost">Schließen</button>
            <button data-testid="save-staff-btn" onClick={save} className="mv-btn"><Save className="w-4 h-4" /> Speichern</button>
          </div>
        </div>
        <div className="mv-card p-6 space-y-4">
          <div><label className="block text-xs mb-1">Name</label><input className="mv-input" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-xs mb-1">Rolle</label><input className="mv-input" value={editing.role || ""} onChange={(e) => setEditing({ ...editing, role: e.target.value })} placeholder="z.B. Top-Stylistin" /></div>
            <div><label className="block text-xs mb-1">E-Mail (intern)</label><input type="email" className="mv-input" value={editing.email || ""} onChange={(e) => setEditing({ ...editing, email: e.target.value })} /></div>
          </div>
          <div><label className="block text-xs mb-1">Foto-URL</label>
            <div className="flex gap-2 items-center">
              {editing.photo_url && <img src={editing.photo_url} alt="" className="w-14 h-14 rounded-full object-cover border" style={{ borderColor: "var(--mv-border)" }} />}
              <input className="mv-input flex-1" value={editing.photo_url || ""} onChange={(e) => setEditing({ ...editing, photo_url: e.target.value })} placeholder="https://..." />
              <label className="mv-btn-ghost text-xs cursor-pointer whitespace-nowrap">
                <Upload className="w-3 h-3" /> Hochladen
                <input
                  type="file" accept="image/*" className="hidden" data-testid="staff-photo-upload"
                  onChange={async (e) => {
                    const file = e.target.files?.[0]; if (!file) return;
                    const fd = new FormData(); fd.append("file", file);
                    const { data } = await api.post("/cms/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                    setEditing((s) => ({ ...s, photo_url: data.url }));
                  }}
                />
              </label>
            </div>
          </div>
          <div><label className="block text-xs mb-1">Telefon (öffentlich, optional)</label><input className="mv-input" value={editing.phone || ""} onChange={(e) => setEditing({ ...editing, phone: e.target.value })} placeholder="+49 …" data-testid="staff-phone" /></div>
          <div><label className="block text-xs mb-1">Bio / Kurztext (öffentlich)</label><textarea className="mv-input min-h-[100px]" value={editing.bio || ""} onChange={(e) => setEditing({ ...editing, bio: e.target.value })} placeholder="Worauf bist du spezialisiert? Was treibt dich an?" data-testid="staff-bio" /></div>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} /> Aktiv (buchbar)</label>

          <div className="pt-4 mt-2 border-t" style={{ borderColor: "var(--mv-border)" }}>
            <p className="mv-label mb-3">Social Media & Web</p>
            <div className="grid md:grid-cols-2 gap-3">
              <SocialInput icon={Instagram} label="Instagram" value={editing.instagram_url} onChange={(v) => setEditing({ ...editing, instagram_url: v })} testId="staff-instagram" />
              <SocialInput icon={Facebook} label="Facebook" value={editing.facebook_url} onChange={(v) => setEditing({ ...editing, facebook_url: v })} testId="staff-facebook" />
              <SocialInput icon={Phone} label="TikTok" value={editing.tiktok_url} onChange={(v) => setEditing({ ...editing, tiktok_url: v })} testId="staff-tiktok" placeholder="https://tiktok.com/@..." />
              <SocialInput icon={Linkedin} label="LinkedIn" value={editing.linkedin_url} onChange={(v) => setEditing({ ...editing, linkedin_url: v })} testId="staff-linkedin" />
              <SocialInput icon={Globe} label="Website" value={editing.website_url} onChange={(v) => setEditing({ ...editing, website_url: v })} testId="staff-website" placeholder="https://..." />
            </div>
          </div>

          <div className="pt-4 mt-4 border-t" style={{ borderColor: "var(--mv-border)" }}>
            <p className="mv-label mb-3">Login-Zugang (optional)</p>
            <div className="grid md:grid-cols-2 gap-3">
              <div><label className="block text-xs mb-1">Login-E-Mail</label><input type="email" data-testid="staff-user-email" className="mv-input" value={editing.user_email || ""} onChange={(e) => setEditing({ ...editing, user_email: e.target.value })} placeholder="anna@..." /></div>
              <div>
                <label className="block text-xs mb-1">Rolle</label>
                <select className="mv-input" value={editing.user_role || "staff_member"} onChange={(e) => setEditing({ ...editing, user_role: e.target.value })}>
                  <option value="staff_member">Mitarbeiter (eigener Kalender)</option>
                  <option value="staff_manager">Manager (Buchungen + Content)</option>
                  <option value="salon_admin">Salon-Admin (Vollzugriff)</option>
                </select>
              </div>
              <div className="md:col-span-2"><label className="block text-xs mb-1">Passwort {editing.id && <span className="opacity-60">(leer = unverändert)</span>}</label><input type="password" data-testid="staff-user-password" className="mv-input" value={editing.user_password || ""} onChange={(e) => setEditing({ ...editing, user_password: e.target.value })} /></div>
            </div>
            {editing.user_email && <p className="text-xs mt-2" style={{ color: "var(--mv-text-muted)" }}>Mitarbeiter kann sich mit diesen Daten bei StudioMV einloggen.</p>}
          </div>
        </div>

        {editing.id && (
          <div className="mv-card p-6 mt-6" data-testid="staff-blocks-panel">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="mv-label">Abwesenheit / Urlaub / Krankheit</p>
                <p className="text-xs" style={{ color: "var(--mv-text-muted)" }}>Im gewählten Zeitraum nicht buchbar.</p>
              </div>
              <CalendarOff className="w-5 h-5" strokeWidth={1.5} style={{ color: "var(--mv-text-muted)" }} />
            </div>

            <div className="space-y-2 mb-4">
              {blocks.length === 0 ? (
                <p className="text-sm text-center py-4" style={{ color: "var(--mv-text-muted)" }}>Keine Abwesenheiten geplant.</p>
              ) : blocks.map((b) => {
                const t = BLOCK_TYPES.find((x) => x.value === b.type) || BLOCK_TYPES[3];
                const Icon = t.icon;
                return (
                  <div key={b.id} className="flex items-center gap-3 p-3 rounded-lg border" style={{ borderColor: "var(--mv-border)" }} data-testid={`block-${b.id}`}>
                    <Icon className="w-4 h-4 flex-shrink-0" strokeWidth={1.5} style={{ color: t.color }} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">
                        {new Date(b.start_date).toLocaleDateString("de-DE")} – {new Date(b.end_date).toLocaleDateString("de-DE")}
                        <span className="ml-2 text-xs font-normal" style={{ color: t.color }}>{t.label}</span>
                      </p>
                      {b.reason && <p className="text-xs opacity-75 truncate">{b.reason}</p>}
                    </div>
                    <button onClick={() => removeBlock(b.id)} className="p-1.5 hover:bg-red-50 rounded" data-testid={`delete-block-${b.id}`} aria-label="Löschen">
                      <Trash2 className="w-3.5 h-3.5 text-red-500" strokeWidth={1.6} />
                    </button>
                  </div>
                );
              })}
            </div>

            <div className="grid md:grid-cols-[1fr_1fr_140px_auto] gap-2 items-end pt-4 border-t" style={{ borderColor: "var(--mv-border)" }}>
              <div>
                <label className="block text-xs mb-1">Von</label>
                <input type="date" data-testid="block-start" className="mv-input text-sm" value={newBlock.start_date} onChange={(e) => setNewBlock({ ...newBlock, start_date: e.target.value })} />
              </div>
              <div>
                <label className="block text-xs mb-1">Bis</label>
                <input type="date" data-testid="block-end" className="mv-input text-sm" value={newBlock.end_date} onChange={(e) => setNewBlock({ ...newBlock, end_date: e.target.value })} />
              </div>
              <div>
                <label className="block text-xs mb-1">Typ</label>
                <select data-testid="block-type" className="mv-input text-sm" value={newBlock.type} onChange={(e) => setNewBlock({ ...newBlock, type: e.target.value })}>
                  {BLOCK_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <button onClick={addBlock} data-testid="add-block-btn" disabled={!newBlock.start_date || !newBlock.end_date} className="mv-btn text-sm h-[38px]">
                <Plus className="w-3.5 h-3.5" /> Hinzufügen
              </button>
              <input
                className="mv-input text-sm md:col-span-4"
                placeholder="Notiz (optional, z. B. Sommerurlaub, Friseur-Messe …)"
                value={newBlock.reason}
                onChange={(e) => setNewBlock({ ...newBlock, reason: e.target.value })}
              />
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="p-8 lg:p-12 mv-fade-in">
      <div className="flex items-end justify-between mb-10">
        <div>
          <p className="mv-label mb-2">Team</p>
          <h1 className="font-display text-4xl">{t("staff_plural")}</h1>
          {limit && (
            <p className="text-xs mt-2" data-testid="staff-limit-info" style={{ color: limit.unlimited ? "var(--mv-text-muted)" : (limit.remaining === 0 ? "#dc2626" : "var(--mv-text-muted)") }}>
              {limit.unlimited
                ? `${limit.current} Mitarbeitende (unbegrenzt im aktuellen Paket)`
                : `${limit.current} / ${limit.limit} Mitarbeitende${limit.remaining === 0 ? " — Limit erreicht. Upgrade nötig, um mehr anzulegen." : ""}`}
            </p>
          )}
        </div>
        <button
          data-testid="new-staff-btn"
          onClick={() => openEdit({ name: "", role: "Stylist", email: "", photo_url: "", active: true })}
          className="mv-btn"
          disabled={limit && !limit.unlimited && limit.remaining === 0}
          title={limit && !limit.unlimited && limit.remaining === 0 ? "Paket-Limit erreicht" : ""}
        >
          {limit && !limit.unlimited && limit.remaining === 0 ? <Lock className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
          Neuer Mitarbeiter
        </button>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {items.map((s) => (
          <div key={s.id} onClick={() => openEdit(s)} className="mv-card p-5 cursor-pointer flex items-center gap-4" data-testid={`staff-card-${s.id}`}>
            <div className="w-14 h-14 rounded-full bg-[var(--mv-surface)] flex items-center justify-center overflow-hidden flex-shrink-0">
              {s.photo_url ? <img src={s.photo_url} alt="" className="w-full h-full object-cover" /> : <UserIcon className="w-6 h-6" strokeWidth={1.5} style={{ color: "var(--mv-text-muted)" }} />}
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-display text-lg truncate">{s.name}</h3>
              <p className="text-xs" style={{ color: "var(--mv-text-muted)" }}>{s.role}</p>
              <p className="text-xs mt-1" style={{ color: s.active ? "var(--mv-secondary)" : "var(--mv-text-muted)" }}>{s.active ? "● Aktiv" : "○ Inaktiv"}</p>
            </div>
            <button onClick={(e) => { e.stopPropagation(); remove(s.id); }} className="text-xs text-red-500"><Trash2 className="w-4 h-4" strokeWidth={1.5} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}


function SocialInput({ icon: Icon, label, value, onChange, testId, placeholder }) {
  return (
    <div>
      <label className="block text-xs mb-1 flex items-center gap-1.5"><Icon className="w-3 h-3" strokeWidth={1.6} /> {label}</label>
      <input
        className="mv-input text-sm"
        data-testid={testId}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder || `https://${label.toLowerCase()}.com/...`}
      />
    </div>
  );
}
